/**
 * Spread proposal page: /proposals/new and /proposals/:proposalId.
 *
 * Owns the project state for one proposal, loading it from RDS when reopening
 * an existing one and writing it back on save. The flow itself (steps, pricing)
 * lives in SpreadFlow; this page only handles which project is in it and where
 * it gets persisted.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CRC } from '../prototype/moduleInputsData';
import { ProjectState } from '../prototype/spreadSplitData';
import { initialProject } from '../prototype/spreadSeed';
import { SpreadFlow } from '../prototype/SpreadFlow';
import { useLiveDefaults } from '../prototype/spreadLiveConfig';
import { fromFormData, saveSpreadProposal } from '../prototype/spreadProposalStore';
import { proposalService } from '../services/proposalService';

type LoadState =
  | { status: 'loading' }
  // savedId is the row this project writes back to. Absent until the first save.
  | { status: 'ready'; project: ProjectState; savedId?: string }
  | { status: 'error'; message: string };

const page: React.CSSProperties = {
  background: CRC.pageBg, minHeight: '100%',
  fontFamily: 'Montserrat, sans-serif', padding: '24px 16px 60px',
};
const notice: React.CSSProperties = {
  maxWidth: 1180, margin: '0 auto', background: '#fff',
  border: `1px solid ${CRC.border}`, borderRadius: 12, padding: 20, fontSize: 13,
};

const SpreadProposalPage: React.FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();
  const [load, setLoad] = useState<LoadState>(
    proposalId ? { status: 'loading' } : { status: 'ready', project: initialProject }
  );

  // Reopening: the list endpoint carries form_data, so the saved project comes
  // back without a dedicated fetch-one route.
  useEffect(() => {
    if (!proposalId) return;
    let cancelled = false;
    proposalService.getProposals()
      .then((records) => {
        if (cancelled) return;
        const record = records.find((r) => r.id === proposalId);
        if (!record) {
          setLoad({ status: 'error', message: 'That proposal could not be found. It may have been deleted.' });
          return;
        }
        const project = fromFormData(record.formData);
        if (!project) {
          setLoad({ status: 'error', message: 'That proposal was saved in the older format and cannot be opened here.' });
          return;
        }
        setLoad({ status: 'ready', project, savedId: record.id });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Could not load the proposal.';
        setLoad({ status: 'error', message });
      });
    return () => { cancelled = true; };
  }, [proposalId]);

  // Show the engine's real config as the drawer defaults (MT + NDT + Coating).
  const live = useLiveDefaults();
  useEffect(() => {
    if (!live) return;
    setLoad((prev) => (prev.status !== 'ready'
      ? prev
      : { ...prev, project: { ...prev.project, liveDefaults: { ...prev.project.liveDefaults, ...live } } }));
  }, [live]);

  const setProject = useCallback((project: ProjectState) => {
    setLoad((prev) => (prev.status === 'ready' ? { ...prev, project } : prev));
  }, []);

  const handleSave = useCallback(async (totalPrice: number) => {
    if (load.status !== 'ready') return;
    // The first save creates the proposal and the page adopts its id, so every
    // later save (including one per step) overwrites that same row.
    const saved = await saveSpreadProposal({ project: load.project, totalPrice, proposalId: load.savedId });
    setLoad((prev) => (prev.status === 'ready' ? { ...prev, savedId: saved.id } : prev));
    if (!proposalId) navigate(`/proposals/${saved.id}`, { replace: true });
  }, [load, navigate, proposalId]);

  if (load.status === 'loading') {
    return <div style={page}><div style={notice}>Loading proposal…</div></div>;
  }
  if (load.status === 'error') {
    return (
      <div style={page}>
        <div style={notice}>
          <div style={{ color: CRC.readableRed, fontWeight: 600, marginBottom: 8 }}>{load.message}</div>
          <button onClick={() => navigate('/proposals')}
            style={{ padding: '8px 14px', border: `1px solid ${CRC.border}`, borderRadius: 8, background: '#fff', color: CRC.blueprintBlue, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
            Back to proposals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <SpreadFlow
        project={load.project}
        setProject={setProject}
        onSave={handleSave}
        saveLabel={load.savedId ? 'Save now' : 'Save proposal'}
        autoSave={!!load.savedId}
      />
    </div>
  );
};

export default SpreadProposalPage;
